import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  bootstrapSeo,
  syncLogostream,
  syncVisaAll,
  refreshTierFares,
  recomputeLinks,
  syncRouteAirlines,
  applyTierManifests,
  syncCountryLogostream,
  refreshTierBaggage,
  warmTier1Caches,
  fetchTierManifests,
  fetchSyncStatus,
  generateAllPages,
  bulkApprovePages,
  formatSeoApiError,
} from '../../../utils/adminSeo';
import { bandClass, bandFromPct, parseActionResult } from './syncActionMappers';

function Btn({ label, onClick, variant = 'default', disabled = false, loading = false }) {
  const cls =
    variant === 'primary'
      ? 'px-4 py-2 rounded-lg text-sm bg-[#1B375B] text-white hover:opacity-90 disabled:opacity-50'
      : 'px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50';
  return (
    <button type="button" className={cls} onClick={onClick} disabled={disabled || loading}>
      {loading ? 'Running…' : label}
    </button>
  );
}

function StatusCard({ title, value, sub, band }) {
  return (
    <div className={`border rounded-lg p-3 text-sm ${bandClass(band)}`}>
      <div className="text-xs opacity-80">{title}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs mt-0.5 opacity-90">{sub}</div>}
    </div>
  );
}

function SyncStatusStrip({ status, loading, onRefresh, highlight }) {
  if (!status && !loading) {
    return (
      <p className="text-sm text-gray-500">
        Status unavailable.{' '}
        <button type="button" className="underline text-[#659A9B]" onClick={onRefresh}>
          Refresh status
        </button>
      </p>
    );
  }
  const t1 = status?.tier1 || {};
  const hl = highlight || '';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium text-[#1B375B]">Pipeline health (T1)</h3>
        <Btn label="Refresh status" onClick={onRefresh} disabled={loading} loading={loading} />
        <Link to="/admin/seo/api-failures" className="text-xs text-[#659A9B] underline">
          API failures ({status?.recent_failures_total_24h ?? 0} / 24h)
        </Link>
        <Link to="/admin/seo/cron-runs" className="text-xs text-[#659A9B] underline">
          Cron runs
        </Link>
      </div>
      <p className="text-xs text-gray-600">
        Airline cache (Logostream / offline / TripJack) = who flies the route. Fare cache (TripJack /
        offline) = prices. They are separate collections.
      </p>
      {status?.logostream_breaker_open && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          Logostream circuit breaker is OPEN — Logostream sync may skip calls.
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <StatusCard
          title="Fresh fare cache"
          value={`${t1.routes_with_fresh_fare_cache ?? 0}/${t1.routes_total ?? 0}`}
          sub={`${t1.fresh_fare_pct ?? 0}% · TripJack/offline`}
          band={hl === 'fares' ? 'ok' : bandFromPct(t1.fresh_fare_pct ?? 0)}
        />
        <StatusCard
          title="Any fare rows"
          value={`${t1.routes_with_any_fare_cache ?? 0}/${t1.routes_total ?? 0}`}
          sub="includes stale"
          band={bandFromPct(
            t1.routes_total
              ? (100 * (t1.routes_with_any_fare_cache || 0)) / t1.routes_total
              : 0,
          )}
        />
        <StatusCard
          title="Airline cache"
          value={`${t1.routes_with_airline_cache ?? 0}/${t1.routes_total ?? 0}`}
          sub={JSON.stringify(t1.airline_cache_by_source || {})}
          band={hl === 'airlines' ? 'ok' : bandFromPct(t1.airline_cache_pct ?? 0)}
        />
        <StatusCard
          title="Baggage cache"
          value={`${t1.routes_with_baggage_cache ?? 0}/${t1.routes_total ?? 0}`}
          sub={`${t1.baggage_cache_pct ?? 0}%`}
          band={hl === 'baggage' ? 'ok' : bandFromPct(t1.baggage_cache_pct ?? 0)}
        />
        <StatusCard
          title="Visa (not pending)"
          value={`${(t1.visa_active ?? 0) - (t1.visa_pending_source ?? 0)}/${t1.visa_active ?? 0}`}
          sub={`${t1.visa_pending_source ?? 0} still pending`}
          band={
            t1.visa_pending_source > 0 && t1.visa_active
              ? 'warn'
              : 'ok'
          }
        />
        <StatusCard
          title="Countries (Logostream)"
          value={`${t1.countries_logostream_ok ?? 0}/${t1.countries_total_t1 ?? 0}`}
          sub={`${t1.countries_logostream_pct ?? 0}%`}
          band={bandFromPct(t1.countries_logostream_pct ?? 0)}
        />
      </div>
      {status?.generated_at && (
        <p className="text-xs text-gray-400">As of {new Date(status.generated_at).toLocaleString()}</p>
      )}
    </div>
  );
}

function LastResultPanel({ result, onClose }) {
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    setShowRaw(false);
  }, [result?.at, result?.label]);

  if (!result) return null;
  const statusLabel = result.ok === true ? 'Success' : result.partial ? 'Partial' : 'Failed';
  const statusCls =
    result.ok === true
      ? 'text-green-700'
      : result.partial
        ? 'text-amber-700'
        : 'text-red-700';

  return (
    <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
      <div className="flex justify-between items-start gap-2">
        <div>
          <strong className="text-[#1B375B]">Last run: {result.label}</strong>
          <span className={`ml-2 text-xs font-medium ${statusCls}`}>{statusLabel}</span>
          {result.durationMs != null && (
            <span className="ml-2 text-xs text-gray-500">{(result.durationMs / 1000).toFixed(1)}s</span>
          )}
          {result.at && (
            <span className="ml-2 text-xs text-gray-400">{new Date(result.at).toLocaleTimeString()}</span>
          )}
        </div>
        <button type="button" className="text-xs text-gray-500" onClick={onClose}>
          Dismiss
        </button>
      </div>
      <ul className="list-disc list-inside text-gray-700 space-y-0.5">
        {result.lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      <div className="flex gap-3 text-xs">
        <Link to="/admin/seo/api-failures" className="text-[#659A9B] underline">
          View API failures
        </Link>
        <Link to="/admin/seo/cron-runs" className="text-[#659A9B] underline">
          Cron runs
        </Link>
        <Link to="/admin/seo/fares" className="text-[#659A9B] underline">
          Fare cache manager
        </Link>
        <button type="button" className="underline" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? 'Hide' : 'Show'} raw JSON
        </button>
      </div>
      {showRaw && result.raw && (
        <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-48">
          {JSON.stringify(result.raw, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function SeoDataImport() {
  const [manifest, setManifest] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [running, setRunning] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [statusHighlight, setStatusHighlight] = useState('');

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const data = await fetchSyncStatus();
      setSyncStatus(data);
    } catch (e) {
      toast.error(formatSeoApiError(e));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTierManifests().then(setManifest).catch(() => {});
    refreshStatus();
  }, [refreshStatus]);

  const reloadManifest = () =>
    fetchTierManifests().then(setManifest).catch(() => {});

  const run = async (label, fn, { highlight = '' } = {}) => {
    setRunning(label);
    const t0 = performance.now();
    let res = null;
    let errMsg = null;
    try {
      res = await fn();
      const parsed = parseActionResult(label, res, null);
      parsed.durationMs = Math.round(performance.now() - t0);
      parsed.at = new Date().toISOString();
      setLastResult(parsed);
      if (parsed.ok === true) toast.success(`${label} OK`);
      else if (parsed.partial) toast.warning(`${label} — partial (see panel)`);
      else toast.error(`${label} — check panel`);
    } catch (e) {
      errMsg = formatSeoApiError(e);
      const parsed = parseActionResult(label, null, errMsg);
      parsed.durationMs = Math.round(performance.now() - t0);
      parsed.at = new Date().toISOString();
      setLastResult(parsed);
      toast.error(errMsg);
    } finally {
      setRunning(null);
      if (highlight) {
        setStatusHighlight(highlight);
        setTimeout(() => setStatusHighlight(''), 8000);
      }
      await refreshStatus();
      if (label.includes('Apply tiers') && res) reloadManifest();
    }
    return res;
  };

  const checkOnly = (highlight) => {
    setStatusHighlight(highlight);
    refreshStatus();
    toast.message('Status refreshed');
  };

  const busy = Boolean(running);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#1B375B]">Data Import & Sync</h2>
      <p className="text-sm text-gray-600">Cache-first — supplier APIs run in background only.</p>

      <div className="bg-gray-50 border rounded-lg p-4">
        <SyncStatusStrip
          status={syncStatus}
          loading={statusLoading}
          onRefresh={refreshStatus}
          highlight={statusHighlight}
        />
      </div>

      <LastResultPanel result={lastResult} onClose={() => setLastResult(null)} />

      {manifest && (
        <div className="bg-white border rounded-lg p-4 text-sm">
          <h3 className="font-medium text-[#1B375B] mb-2">Tier manifests (version-controlled)</h3>
          <pre className="text-xs overflow-auto max-h-32 bg-gray-50 p-2 rounded">
            {JSON.stringify(manifest.summary || manifest.manifest_keys, null, 2)}
          </pre>
          <p className="text-xs text-gray-500 mt-1">
            Overrides in DB: {manifest.override_count ?? 0}
          </p>
        </div>
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700">Setup</h3>
        <div className="flex flex-wrap gap-2">
          <Btn
            label="Bootstrap"
            disabled={busy}
            loading={running === 'Bootstrap'}
            onClick={() => run('Bootstrap', bootstrapSeo)}
          />
          <Btn
            label="Apply tier manifests"
            variant="primary"
            disabled={busy}
            loading={running === 'Apply tiers'}
            onClick={() => run('Apply tiers', applyTierManifests)}
          />
          <Btn
            label="Logostream sync"
            disabled={busy}
            loading={running === 'Logostream'}
            onClick={() => run('Logostream', () => syncLogostream('all'))}
          />
          <Btn
            label="Sync T1 countries (Logostream)"
            disabled={busy}
            loading={running === 'Country Logostream'}
            onClick={() => run('Country Logostream', () => syncCountryLogostream())}
          />
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700">Tier 1 launch caches</h3>
        <div className="flex flex-wrap gap-2">
          <Btn
            label="Warm all T1 caches"
            variant="primary"
            disabled={busy}
            loading={running === 'Warm T1'}
            onClick={() => run('Warm T1', warmTier1Caches, { highlight: 'fares' })}
          />
          <Btn
            label="Fares tier 1"
            disabled={busy}
            loading={running === 'Fares T1'}
            onClick={() => run('Fares T1', () => refreshTierFares(1), { highlight: 'fares' })}
          />
          <Btn
            label="Check fare coverage"
            disabled={busy}
            onClick={() => checkOnly('fares')}
          />
          <Btn
            label="Fares tier 2"
            disabled={busy}
            loading={running === 'Fares T2'}
            onClick={() => run('Fares T2', () => refreshTierFares(2))}
          />
          <Btn
            label="Route airlines"
            disabled={busy}
            loading={running === 'Route airlines'}
            onClick={() => run('Route airlines', () => syncRouteAirlines({ tier: 1, limit: 55 }), {
              highlight: 'airlines',
            })}
          />
          <Btn label="Check airline cache" disabled={busy} onClick={() => checkOnly('airlines')} />
          <Btn
            label="Baggage T1"
            disabled={busy}
            loading={running === 'Baggage T1'}
            onClick={() => run('Baggage T1', () => refreshTierBaggage(1), { highlight: 'baggage' })}
          />
          <Btn label="Check baggage coverage" disabled={busy} onClick={() => checkOnly('baggage')} />
          <Btn
            label="Baggage T2"
            disabled={busy}
            loading={running === 'Baggage T2'}
            onClick={() => run('Baggage T2', () => refreshTierBaggage(2))}
          />
          <Btn
            label="Visa sync"
            disabled={busy}
            loading={running === 'Visa'}
            onClick={() => run('Visa', syncVisaAll)}
          />
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700">Pages (draft → approve)</h3>
        <div className="flex flex-wrap gap-2">
          <Btn
            label="Generate T1/T2 pages"
            disabled={busy}
            loading={running === 'Generate pages'}
            onClick={() => run('Generate pages', () => generateAllPages(2))}
          />
          <Btn
            label="Bulk approve drafts"
            disabled={busy}
            loading={running === 'Bulk approve'}
            onClick={() =>
              run('Bulk approve', () => bulkApprovePages({ publish_status: 'draft', limit: 100 }))
            }
          />
          <Btn
            label="Recompute links"
            disabled={busy}
            loading={running === 'Links'}
            onClick={() => run('Links', () => recomputeLinks(true))}
          />
        </div>
        <p className="text-xs text-gray-500">
          Review failures in Pages / Content Audit. Visa pages need live sync (not pending) before
          approve.
        </p>
      </section>
    </div>
  );
}
